import get from 'lodash/get'
import React, { useCallback, useEffect, ReactNode, useState, Fragment } from 'react'
import { BContainer, BTitle } from '@prague-digi/ui-components'
import { compose } from 'recompose'
import { connect, ConnectedProps } from 'react-redux'
import { FormattedMessage } from 'react-intl'

import { appSelectors, modalDialogActions } from '../../../../redux'
import { intl, IntlInterface } from '../../../../hoc'
import { OUIBOUNCE_MODAL } from '../../../../constants/modals'
import { ReduxState } from '../../../../types'
import { AvailabilityStatus, Routes } from '../../../../constants'
import { SUPPORT_PHONE } from '../../../../constants/commonConstants'
import messages from '../../../../i18n'
import monetaLogo from '../../../../assets/monetaLogo.svg'
import NotificationContainer from '../../../Notification/Notification'
import { Notification } from '../../../../components'
import phoneIcon from '../../../../assets/phone.svg'
import RouteHelper from '../RouteHelper/RouteHelper'
import SidePanel from '../SidePanel/SidePanel'
import SaveAndLeaveButton from '../SaveAndLeaveButton/SaveAndLeaveButton'

import styles from './styles.scss'
import { isoToCzDate, isoToCzTime } from '../../../../utils/dates'

interface Props extends IntlInterface, ConnectedProps<typeof connector> {
  children: ReactNode
  registerMiddleContainerElement: any
}

export const showOuibounceModalOnPath = (actualPath: string): boolean =>
  ![
    Routes.AVAILABILITY,
    Routes.CLIENT_INFO,
    Routes.EXIT,
    Routes.FINAL,
    Routes.KO_ONLINE,
    Routes.KO_SCORING,
    Routes.LOAN_CONFIGURATOR,
  ].includes(actualPath)

export const BasicLayout = ({
  actualPath,
  children,
  isDevelopment,
  availability,
  registerMiddleContainerElement,
  showModalDialog,
  translate,
}: Props) => {
  const [canShowOuibounceModal, disableOuibounceModal] = useState(!isDevelopment)

  const handleMouseleave = useCallback(
    (e: MouseEvent) => {
      if (e.clientY < 20) {
        if (canShowOuibounceModal && showOuibounceModalOnPath(actualPath)) {
          disableOuibounceModal(false)
          showModalDialog(OUIBOUNCE_MODAL)
        }
      }
    },
    [actualPath, canShowOuibounceModal, showModalDialog]
  )

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseleave)

    // returned function will be called on component unmount
    return () => {
      window.removeEventListener('mousemove', handleMouseleave)
    }
  }, [canShowOuibounceModal, actualPath, handleMouseleave])

  const showRouteHelper = () => {
    isDevelopment &&
      showModalDialog({
        title: 'Helper',
        content: <RouteHelper />,
      })
  }

  return (
    <BContainer className={styles.BasicLayout}>
      <div className={styles.topContainer}>
        <div role="button" tabIndex={0} onClick={showRouteHelper} className={styles.monetaLogo}>
          <img src={monetaLogo} alt="moneta_logo" />
        </div>
        <div className={styles.pageTitle}>
          <BTitle type="h4">{translate(messages.basicLayout.pageTitle)}</BTitle>
        </div>
        <div className={styles.support}>
          <img src={phoneIcon} alt="phone_icon" className={styles.phoneIcon} />
          <BTitle type="h4" theme={styles.phoneNumber}>
            {SUPPORT_PHONE}
          </BTitle>
        </div>
      </div>
      {availability?.status === AvailabilityStatus.PlannedOutage &&
        availability?.outageEnd &&
        availability.outageStart && (
          <Notification
            // isMarginBottom={false}
            description={
              <Fragment>
                <FormattedMessage
                  {...messages.availability.notification}
                  values={{
                    start: <b>{isoToCzTime(availability.outageStart)}</b>,
                    endDay: <b>{isoToCzDate(availability.outageEnd)}</b>,
                    endTime: <b>{isoToCzTime(availability.outageEnd)}</b>,
                  }}
                />
              </Fragment>
            }
          />
        )}

      <NotificationContainer />

      <div
        className={styles.middleContainer}
        ref={(self: HTMLDivElement) => registerMiddleContainerElement(self)}>
        {children && <div className={styles.contentContainer}>{children}</div>}
        <SidePanel />
        <div className={styles.saveAndLeaveButtonWrapper}>
          <SaveAndLeaveButton />
        </div>
      </div>
    </BContainer>
  )
}

const connector = connect(
  (state: ReduxState) => ({
    actualPath: get(state, 'router.location.pathname'),
    isDevelopment: appSelectors.selectIsDevelopment(state),
    availability: state.app.availability,
  }),
  {
    showModalDialog: modalDialogActions.showModalDialog,
  }
)

export default compose(connector, intl)(BasicLayout)
